/**
 * Debris — instanced physical chunks with gravity, bounce and tumble.
 *
 * Sprites can fake a lot, but a chunk of deck that is thrown, lands, bounces
 * once and skids to a stop — casting a shadow the whole way on the tiers that
 * have shadows — is what gives a slam WEIGHT. One `InstancedMesh` of a unit
 * cube, toon-shaded to match the arena, tinted per instance; every piece is a
 * matrix rewritten each frame from a small state table, so a hundred pieces
 * are one draw and one buffer upload.
 *
 * Pieces are compacted (swap-remove) so `count` is exactly the live number and
 * a still scene draws nothing. The last 0.4 s of a piece's life shrinks it to
 * nothing: an opaque instanced mesh cannot fade, and a chunk that pops out of
 * existence at full size is the thing that reads as a bug.
 */

import * as THREE from 'three/webgpu';
import { GRADIENT_2 } from '../ToonMaterials.js';

const GRAVITY = 19;
const RESTITUTION = 0.38;
const FADE_S = 0.4;

/** Floats per piece: life x y z vx vy vz rx ry rz wx wy wz size floor r g b. */
const S = 17;
const LIFE = 0;
const PX = 1;
const VX = 4;
const RX = 7;
const WX = 10;
const SIZE = 13;
const FLOOR = 14;
const COL = 15;

const _m = new THREE.Matrix4();
const _p = new THREE.Vector3();
const _q = new THREE.Quaternion();
const _e = new THREE.Euler();
const _s = new THREE.Vector3();
const _c = new THREE.Color();

export class Debris {
  /**
   * @param {number} capacity pieces allocated once (the ultra budget)
   */
  constructor(capacity) {
    this.capacity = capacity;
    this.cap = capacity;
    this.live = 0;
    this.state = new Float32Array(capacity * S);
    const material = new THREE.MeshToonNodeMaterial({ color: 0xffffff, gradientMap: GRADIENT_2 });
    this.mesh = new THREE.InstancedMesh(new THREE.BoxGeometry(1, 1, 1), material, capacity);
    this.mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.mesh.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(capacity * 3), 3);
    this.mesh.instanceColor.setUsage(THREE.DynamicDrawUsage);
    this.mesh.count = 0;
    this.mesh.visible = false;
    this.mesh.frustumCulled = false;
    this.mesh.castShadow = true;
    this.mesh.receiveShadow = true;
    this.mesh.name = 'debris';
    this.stats = { spawned: 0, evicted: 0 };
  }

  attach(scene) {
    scene.add(this.mesh);
    return this;
  }

  setCap(n) {
    this.cap = Math.max(1, Math.min(this.capacity, Math.floor(n)));
    while (this.live > this.cap) this._removeAt(this.live - 1);
    this._publish();
  }

  /** Shadows cost a pass on the tiers that have them; the caller decides. */
  setShadows(on) {
    this.mesh.castShadow = !!on;
  }

  /**
   * Throw `count` pieces from a point. Colour is a hex or a THREE.Color; each
   * piece jitters its lightness so a pile is not a stamped pattern.
   */
  spawn(x, y, z, color, count, floor = y, { speed = 4.2, up = 7, size = 0.27, life = 2.6 } = {}) {
    _c.set(color);
    const n = Math.min(count, this.cap);
    for (let k = 0; k < n; k++) {
      let i = this.live;
      if (i >= this.cap) {
        // Full: evict the piece nearest the end of its life.
        let victim = 0;
        for (let j = 1; j < this.live; j++) {
          if (this.state[j * S + LIFE] < this.state[victim * S + LIFE]) victim = j;
        }
        i = victim;
        this.stats.evicted++;
      } else {
        this.live++;
      }
      this.stats.spawned++;
      const o = i * S;
      const st = this.state;
      const a = Math.random() * Math.PI * 2;
      const sp = (0.3 + Math.random() * 0.7) * speed;
      st[o + LIFE] = life * (0.6 + Math.random() * 0.4);
      st[o + PX] = x + (Math.random() - 0.5) * 0.6;
      st[o + PX + 1] = y + Math.random() * 0.4;
      st[o + PX + 2] = z + (Math.random() - 0.5) * 0.6;
      st[o + VX] = Math.cos(a) * sp;
      st[o + VX + 1] = up * (0.45 + Math.random() * 0.55);
      st[o + VX + 2] = Math.sin(a) * sp;
      st[o + RX] = Math.random() * 6;
      st[o + RX + 1] = Math.random() * 6;
      st[o + RX + 2] = Math.random() * 6;
      st[o + WX] = (Math.random() - 0.5) * 18;
      st[o + WX + 1] = (Math.random() - 0.5) * 18;
      st[o + WX + 2] = (Math.random() - 0.5) * 18;
      st[o + SIZE] = size * (0.45 + Math.random() * 0.55);
      st[o + FLOOR] = floor;
      const l = (Math.random() - 0.5) * 0.14;
      st[o + COL] = Math.max(0, _c.r + l);
      st[o + COL + 1] = Math.max(0, _c.g + l);
      st[o + COL + 2] = Math.max(0, _c.b + l);
    }
    this._publish();
  }

  update(dt) {
    if (this.live === 0) return;
    const st = this.state;
    for (let i = this.live - 1; i >= 0; i--) {
      const o = i * S;
      let life = st[o + LIFE];
      if (dt > 0) {
        life -= dt;
        if (life <= 0) {
          this._removeAt(i);
          continue;
        }
        st[o + LIFE] = life;
        st[o + VX + 1] -= GRAVITY * dt;
        st[o + PX] += st[o + VX] * dt;
        st[o + PX + 1] += st[o + VX + 1] * dt;
        st[o + PX + 2] += st[o + VX + 2] * dt;
        st[o + RX] += st[o + WX] * dt;
        st[o + RX + 1] += st[o + WX + 1] * dt;
        st[o + RX + 2] += st[o + WX + 2] * dt;
        const rest = st[o + FLOOR] + st[o + SIZE] * 0.5;
        if (st[o + PX + 1] < rest && st[o + VX + 1] < 0) {
          st[o + PX + 1] = rest;
          st[o + VX + 1] = -st[o + VX + 1] * RESTITUTION;
          st[o + VX] *= 0.7;
          st[o + VX + 2] *= 0.7;
          st[o + WX] *= 0.55;
          st[o + WX + 1] *= 0.55;
          st[o + WX + 2] *= 0.55;
        }
      }
    }
    this._publish();
  }

  _removeAt(i) {
    const last = this.live - 1;
    if (i !== last) this.state.copyWithin(i * S, last * S, last * S + S);
    this.live = last;
  }

  _publish() {
    const st = this.state;
    const colors = this.mesh.instanceColor.array;
    for (let i = 0; i < this.live; i++) {
      const o = i * S;
      const fade = Math.min(1, st[o + LIFE] / FADE_S);
      const size = st[o + SIZE] * fade;
      _p.set(st[o + PX], st[o + PX + 1], st[o + PX + 2]);
      _e.set(st[o + RX], st[o + RX + 1], st[o + RX + 2]);
      _q.setFromEuler(_e);
      _s.set(size, size, size);
      _m.compose(_p, _q, _s);
      this.mesh.setMatrixAt(i, _m);
      colors[i * 3] = st[o + COL];
      colors[i * 3 + 1] = st[o + COL + 1];
      colors[i * 3 + 2] = st[o + COL + 2];
    }
    this.mesh.count = this.live;
    this.mesh.visible = this.live > 0;
    this.mesh.instanceMatrix.needsUpdate = true;
    this.mesh.instanceColor.needsUpdate = true;
  }

  clear() {
    this.live = 0;
    this._publish();
  }

  dispose() {
    this.mesh.removeFromParent();
    this.mesh.geometry.dispose();
    this.mesh.material.dispose();
  }
}
